import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { 
  startOfMonth, 
  endOfMonth, 
  format, 
  isToday, 
  isSameDay,
  parseISO,
  getYear,
  getMonth,
  startOfDay,
  endOfDay
} from 'date-fns';
import { getCommissions, Commission } from '../firebase/financeServices';
import { validateSaleSplits, subscribeToValidationIssues, SaleValidationIssue } from '../services/validationService';

// Helper function to format currency with RM
const formatCurrency = (amount: number): string => {
  return `RM ${amount.toFixed(2)}`;
};

// Helper function to format currency for display in cards (larger font)
const formatCurrencyDisplay = (amount: number): { value: string, rmPart: string, amountPart: string } => {
  const amountStr = amount.toFixed(2);
  
  return {
    value: `RM ${amountStr}`,
    rmPart: 'RM',
    amountPart: amountStr
  };
};

// Helper function to format date
const formatDate = (date: Date | string): string => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'dd MMM yyyy');
};

interface StaffSummary {
  staffId: string;
  staffName: string;
  totalSalesSplit: number;
  serviceCount: number;
  salesSplits: Commission[];
  pendingAmount: number;
  paidAmount: number;
  voidedAmount: number;
}



const SalesSplitBreakdown: React.FC = () => {
  const currentYear = getYear(new Date());
  const currentMonth = getMonth(new Date());
  
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [salesSplits, setSalesSplits] = useState<Commission[]>([]);
  const [filteredSalesSplits, setFilteredSalesSplits] = useState<Commission[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [staffSummaries, setStaffSummaries] = useState<StaffSummary[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [validationIssues, setValidationIssues] = useState<SaleValidationIssue[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Generate years for dropdown (5 years back, 2 years forward)
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  
  // Month names for dropdown
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Status display mapping
  const statusDisplayMap: Record<string, { label: string, color: 'success' | 'warning' | 'error' | 'default' }> = {
    'paid': { label: 'Paid', color: 'success' },
    'pending': { label: 'Pending', color: 'warning' },
    'voided': { label: 'Voided', color: 'error' }
  };

  useEffect(() => {
    fetchSalesSplitData(selectedYear, selectedMonth);
    
    // Set up real-time validation updates
    const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
    const unsubscribe = subscribeToValidationIssues(
      selectedYear,
      selectedMonth,
      staffId,
      (issues) => {
        setValidationIssues(issues);
      }
    );
    
    // Clean up subscription when component unmounts or dependencies change
    return () => {
      unsubscribe();
    };
  }, [selectedYear, selectedMonth, selectedStaff]);

  useEffect(() => {
    filterSalesSplitData();
  }, [salesSplits, searchTerm, statusFilter, selectedStaff]);

  const fetchSalesSplitData = async (year: number, month: number) => {
    setLoading(true);
    setError(null);
    
    try {
      // Create date range for the selected month
      const startDate = startOfMonth(new Date(year, month));
      const endDate = endOfMonth(new Date(year, month));
      
      // Get sales splits from Firebase (filtering by type='sale')
      const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
      const commissionsData = await getCommissions(staffId, startDate, endDate);
      
      // Filter to only include sales splits
      const salesSplitsData = commissionsData.filter(comm => comm.type === 'sale');
      
      setSalesSplits(salesSplitsData);
      
      // Generate staff summaries
      generateStaffSummaries(salesSplitsData);
      
      // Clear previous validation issues
      setValidationIssues([]);
    } catch (err) {
      console.error('Error fetching sales split data:', err);
      setError('Failed to load sales split data. Please try again.');
      enqueueSnackbar('Failed to load sales split data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const generateStaffSummaries = (salesSplitData: Commission[]) => {
    // Group sales splits by staff
    const staffMap = new Map<string, Commission[]>();
    
    salesSplitData.forEach(split => {
      if (!staffMap.has(split.staffId)) {
        staffMap.set(split.staffId, []);
      }
      staffMap.get(split.staffId)?.push(split);
    });
    
    // Create staff summaries
    const summaries: StaffSummary[] = [];
    
    staffMap.forEach((staffSalesSplits, staffId) => {
      if (staffSalesSplits.length > 0) {
        const staffName = staffSalesSplits[0].staffName;
        const totalSalesSplit = staffSalesSplits.reduce((sum, split) => sum + split.amount, 0);
        const serviceCount = staffSalesSplits.length;
        
        // Calculate amounts by status
        const pendingAmount = staffSalesSplits
          .filter(split => split.status === 'pending')
          .reduce((sum, split) => sum + split.amount, 0);
          
        const paidAmount = staffSalesSplits
          .filter(split => split.status === 'paid')
          .reduce((sum, split) => sum + split.amount, 0);
          
        const voidedAmount = staffSalesSplits
          .filter(split => split.status === 'voided')
          .reduce((sum, split) => sum + split.amount, 0);
        
        summaries.push({
          staffId,
          staffName,
          totalSalesSplit,
          serviceCount,
          salesSplits: staffSalesSplits,
          pendingAmount,
          paidAmount,
          voidedAmount
        });
      }
    });
    
    // Sort by total sales split amount (highest first)
    summaries.sort((a, b) => b.totalSalesSplit - a.totalSalesSplit);
    
    setStaffSummaries(summaries);
  };

  const filterSalesSplitData = () => {
    let filtered = [...salesSplits];
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(split => 
        split.staffName.toLowerCase().includes(term) ||
        split.serviceName.toLowerCase().includes(term) ||
        split.saleNumber.toLowerCase().includes(term) ||
        (split.customerName && split.customerName.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(split => split.status === statusFilter);
    }
    
    // Apply staff filter
    if (selectedStaff !== 'all') {
      filtered = filtered.filter(split => split.staffId === selectedStaff);
    }
    
    setFilteredSalesSplits(filtered);
    setPage(0); // Reset to first page when filters change
  };
  
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };
  
  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(event.target.value));
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };
  
  const handleStaffFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedStaff(event.target.value);
  };
  
  const handleRefresh = () => {
    fetchSalesSplitData(selectedYear, selectedMonth);
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handlePrintReport = () => {
    window.print();
  };
  
  const handleValidateSaleSplits = async () => {
    setValidating(true);
    try {
      const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
      const issues = await validateSaleSplits(selectedYear, selectedMonth, staffId);
      
      setValidationIssues(issues);
      setValidationDialogOpen(true);
      
      if (issues.length === 0) {
        enqueueSnackbar('All sales splits match their sale totals. No discrepancies found.', { variant: 'success' });
      }
    } catch (err) {
      console.error('Error validating sales splits:', err);
      enqueueSnackbar('Failed to validate sales splits', { variant: 'error' });
    } finally {
      setValidating(false);
    }
  };
  
  // Calculate totals (excluding voided amounts)
  const totalSalesAmount = filteredSalesSplits
    .filter(split => split.status !== 'voided')
    .reduce((sum, split) => sum + split.amount, 0);
  const totalPendingAmount = filteredSalesSplits
    .filter(split => split.status === 'pending')
    .reduce((sum, split) => sum + split.amount, 0);
  const totalPaidAmount = filteredSalesSplits
    .filter(split => split.status === 'paid')
    .reduce((sum, split) => sum + split.amount, 0);
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Month/Year Selection */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h5" component="h1">
          Sales Split Breakdown
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 120 } }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              {months.map((month, index) => (
                <MenuItem key={month} value={index}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 100 } }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrintReport}
          >
            Print
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary"
            size="small"
            startIcon={validating ? <CircularProgress size={16} /> : <FilterListIcon />}
            onClick={handleValidateSaleSplits}
            disabled={validating || loading}
            sx={{ position: 'relative' }}
          >
            Validate Splits
            {validationIssues.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  border: '2px solid white',
                }}
              >
                {validationIssues.length > 99 ? '99+' : validationIssues.length}
              </Box>
            )}
          </Button>
        </Box>
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Sales</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="primary" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalSalesAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="primary" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalSalesAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredSalesSplits.filter(split => split.status !== 'voided').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Pending Sale Finalize</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="warning.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalPendingAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="warning.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalPendingAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredSalesSplits.filter(c => c.status === 'pending').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Paid Sales Split</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalPaidAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalPaidAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredSalesSplits.filter(c => c.status === 'paid').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Staff Count</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {staffSummaries.length}
              </Typography>
              <Typography variant="body2">
                with sales splits this month
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      {/* Staff Breakdown */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Staff Breakdown</Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>Staff Name</TableCell>
                <TableCell align="right">Services</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Voided</TableCell>
                <TableCell align="right">Total Sales</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staffSummaries.map((staff) => (
                <TableRow key={staff.staffId} hover>
                  <TableCell>{staff.staffName}</TableCell>
                  <TableCell align="right">{staff.serviceCount}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.pendingAmount)}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.paidAmount)}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.voidedAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(staff.totalSalesSplit)}
                  </TableCell>
                </TableRow>
              ))}
              {staffSummaries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No sales split data available</TableCell>
                </TableRow>
              )}
            </TableBody>
            {staffSummaries.length > 0 && (
              <TableHead>
                <TableRow sx={{ 
                  '& th': { fontWeight: 600 },
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">
                    {staffSummaries.reduce((sum, staff) => sum + staff.serviceCount, 0)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.pendingAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.paidAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.voidedAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.totalSalesSplit, 0))}
                  </TableCell>
                </TableRow>
              </TableHead>
            )}
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2 
        }}>
          <Typography variant="h6">Sales Split Details</Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <TextField
              placeholder="Search sales splits..."
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: { xs: '100%', sm: 170 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 120 } }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="voided">Voided</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 150 } }}>
              <InputLabel id="staff-filter-label">Staff</InputLabel>
              <Select
                labelId="staff-filter-label"
                id="staff-filter"
                value={selectedStaff}
                label="Staff"
                onChange={handleStaffFilterChange}
              >
                <MenuItem value="all">All Staff</MenuItem>
                {staffSummaries.map(staff => (
                  <MenuItem key={staff.staffId} value={staff.staffId}>
                    {staff.staffName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Sale #</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Service Price</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Sales Split</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSalesSplits
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((split) => (
                      <TableRow key={split.id} hover>
                        <TableCell>{formatDate(split.date)}</TableCell>
                        <TableCell>{split.saleNumber}</TableCell>
                        <TableCell>{split.staffName}</TableCell>
                        <TableCell>{split.serviceName}</TableCell>
                        <TableCell>{split.customerName || 'Walk-in'}</TableCell>
                        <TableCell align="right">{formatCurrency(split.servicePrice)}</TableCell>
                        <TableCell align="right">{(split.commissionRate * 100).toFixed(0)}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(split.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusDisplayMap[split.status]?.label || split.status}
                            color={statusDisplayMap[split.status]?.color || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {split.paymentDate ? formatDate(split.paymentDate) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredSalesSplits.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">No sales split data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredSalesSplits.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      
      {/* Validation Dialog */}
      <Dialog 
        open={validationDialogOpen} 
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Sales Split Validation Results</Typography>
            <IconButton onClick={() => setValidationDialogOpen(false)} size="small">
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {validationIssues.length === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              All sales splits match their sale totals. No discrepancies found.
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Found {validationIssues.length} sales with discrepancies between sale total and split amounts.
              </Alert>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Sale #</TableCell>
                      <TableCell>Staff</TableCell>
                      <TableCell align="right">Sale Total</TableCell>
                      <TableCell align="right">Split Total</TableCell>
                      <TableCell align="right">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationIssues.map((issue) => (
                      <TableRow key={issue.saleNumber} hover>
                        <TableCell>{formatDate(issue.date)}</TableCell>
                        <TableCell>{issue.saleNumber}</TableCell>
                        <TableCell>{issue.staffNames.join(', ')}</TableCell>
                        <TableCell align="right">{formatCurrency(issue.saleTotal)}</TableCell>
                        <TableCell align="right">{formatCurrency(issue.splitTotal)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                          {formatCurrency(issue.difference)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesSplitBreakdown;
